"use client"
import { motion } from 'framer-motion';
import HeroImage from '../../assets/presentationBuilder/Hero.webp';
import { PartnershipVisuals } from './product-elements/partnership-visuals';
import Link from "next/link";
import Image from "next/image";

const PartnershipProposalBuilder = () => {
    return (
        <div className="w-full bg-white text-black font-jakarta">

            {/* Hero Section */}
            <section className="relative pt-28 pb-20 flex flex-col items-center overflow-visible bg-white text-black">
                {/* Background - Dot Grid */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

                {/* Creative Elements - Background Shapes */}
                <div className="absolute top-20 left-[10%] w-64 h-64 bg-blue-100/50 rounded-full blur-3xl -z-10" />
                <div className="absolute bottom-40 right-[10%] w-72 h-72 bg-indigo-100/50 rounded-full blur-3xl -z-10" />

                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 w-full flex flex-col items-center text-center">

                    {/* H1 & Subtitle Container - Condensed */}
                    <div className="max-w-3xl mx-auto flex flex-col items-center mb-16 mt-10 relative z-20">
                        {/* Heading - Reframed from Products.tsx */}
                        <motion.h1
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="font-syne font-bold text-4xl md:text-[46px] leading-[1.15] tracking-tight text-gray-900 mb-5"
                        >
                            Create Professional <span className="text-[#2563EB] relative inline-block">
                                Partnership Docs
                                <svg className="absolute w-full h-2.5 -bottom-1 left-0 text-blue-200 -z-10" viewBox="0 0 100 10" preserveAspectRatio="none">
                                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                                </svg>
                            </span> Fast
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="font-jakarta text-gray-500 text-lg leading-relaxed max-w-2xl mb-10"
                        >
                            Craft compelling proposals to form alliances and pitch collaborations. AI structures your value proposition and terms in a business-ready format.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="relative group inline-block"
                        >
                            {/* Button Glow - Subtle */}
                            <div className="absolute -inset-0.5 bg-blue-500/30 rounded-full blur opacity-50 group-hover:opacity-75 transition duration-200" />

                            {/* Main CTA Button */}
                            <Link href="/signup" className="relative px-10 py-3.5 bg-[#2563EB] text-white rounded-full font-semibold text-[16px] shadow-sm hover:shadow-md hover:bg-blue-700 transition-all overflow-hidden group/btn ring-1 ring-white/10 inline-block">
                                {/* Ripple Effect Container */}
                                <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-[200%] group-hover/btn:translate-x-[200%] transition-transform duration-1000 ease-in-out" />

                                <span className="relative flex items-center gap-2 justify-center">
                                    Try for free
                                </span>
                            </Link>

                            {/* Scribble Arrow */}
                            <div className="absolute left-[105%] top-[-30%] w-48 hidden md:block pointer-events-none">
                                <svg viewBox="0 0 200 100" fill="none" className="w-full h-full text-gray-800/80">
                                    <path
                                        d="M160 30 C 140 10, 60 10, 30 40"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        className="opacity-90"
                                    />
                                    <path
                                        d="M25 32 L 30 40 L 38 38"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-90"
                                    />
                                    <text
                                        x="50"
                                        y="70"
                                        fontSize="15"
                                        fill="currentColor"
                                        className="font-handwriting font-medium opacity-90"
                                        style={{ fontFamily: 'cursive' }}
                                    >
                                        *No credit card req
                                    </text>
                                </svg>
                            </div>
                        </motion.div>
                    </div>

                    {/* Main Image Container */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="relative w-full flex justify-center px-4 md:px-0"
                    >
                        <div className="relative w-full max-w-5xl mx-auto">
                            {/* Editor Window Frame */}
                            <div className="rounded-xl overflow-hidden border border-gray-200/80 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] bg-white relative ring-1 ring-black/5 mx-auto">
                                {/* Toolbar Mockup */}
                                <div className="bg-[#fcfcfc] border-b border-gray-200 h-10 md:h-11 flex items-center px-4 justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="flex gap-1.5">
                                            <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57] border border-[#E0443E]/50" />
                                            <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E] border border-[#D89E24]/50" />
                                            <div className="w-2.5 h-2.5 rounded-full bg-[#28C840] border border-[#1AAB29]/50" />
                                        </div>
                                        <div className="hidden sm:block h-4 w-px bg-gray-200 mx-1" />
                                        <div className="hidden sm:flex gap-3">
                                            <div className="h-2 w-16 bg-gray-100 rounded-full" />
                                            <div className="h-2 w-10 bg-gray-100 rounded-full" />
                                        </div>
                                    </div>
                                    <div className="h-2 w-16 md:w-20 bg-gray-100 rounded-full" />
                                </div>

                                {/* Actual Asset Image */}
                                <div className="relative bg-gray-50 h-[80vh] w-full">
                                    <Image
                                        src="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1769413061291_2fx4fgcydty_Partnership Deck.png"
                                        alt="Partnership Proposal Interface"
                                        fill
                                        className="object-contain"
                                        priority
                                        sizes="(max-width: 768px) 100vw, 90vw"
                                    />
                                </div>

                                {/* Overlay UI: Cursor */}
                            </div>

                            {/* Decorative background */}
                            <div className="absolute -inset-x-10 -inset-y-10 bg-gradient-to-t from-blue-100/30 via-purple-50/30 to-transparent -z-10 blur-3xl rounded-[50%]" />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Why Founders Use Deckwale for Partnerships Section */}
            <section className="py-24 bg-[#ffffff]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-syne font-bold text-3xl md:text-4xl text-gray-900 mb-4">
                            Why Founders Use Deckwale for Partnerships
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                title: "Clear value communication",
                                desc: "Deckwale structures proposals around mutual benefits and outcomes.",
                                iconColor: "from-blue-400 to-blue-600",
                                shadowColor: "shadow-blue-200",
                                icon: (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>
                                )
                            },
                            {
                                title: "Faster proposal creation",
                                desc: "Generate professional partnership decks without starting from scratch.",
                                iconColor: "from-indigo-400 to-purple-600",
                                shadowColor: "shadow-indigo-200",
                                icon: (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                )
                            },
                            {
                                title: "Business-ready and persuasive",
                                desc: "Present your collaboration idea with clarity and confidence.",
                                iconColor: "from-pink-400 to-rose-600",
                                shadowColor: "shadow-pink-200",
                                icon: (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                                )
                            }
                        ].map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-gray-200/50 hover:shadow-2xl hover:shadow-gray-200/80 transition-all group relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gray-50 to-gray-100 rounded-bl-[100px] -z-10 opacity-50 group-hover:scale-110 transition-transform duration-500" />

                                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.iconColor} flex items-center justify-center shadow-lg ${item.shadowColor} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {item.icon}
                                </div>

                                <h3 className="font-bold text-xl text-gray-900 mb-3 leading-snug">{item.title}</h3>
                                <p className="text-gray-500 leading-relaxed text-sm">
                                    {item.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-start">
                        {/* Left: Visual */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="relative order-2 lg:order-1 flex justify-center sticky top-28"
                        >
                            <div className="relative w-full max-w-md lg:max-w-full">
                                <PartnershipVisuals />
                                {/* Decorative Blob */}
                                <div className="absolute -inset-10 bg-gradient-to-r from-blue-400/20 to-purple-400/20 blur-3xl -z-10 rounded-full opacity-50" />
                            </div>
                        </motion.div>

                        {/* Right: Content - 6 Steps */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="order-1 lg:order-2"
                        >
                            <h2 className="font-syne font-bold text-3xl md:text-4xl text-gray-900 mb-6 leading-tight">
                                How It Works
                            </h2>

                            <div className="space-y-6">
                                {[
                                    { title: "Provide company basics", desc: "Share industry, website, and partnership context." },
                                    { title: "Add core proposal details", desc: "Upload documents or describe collaboration goals." },
                                    { title: "Complete the guided Q&A", desc: "AI ensures no critical section is missed." },
                                    { title: "Review and refine the outline", desc: "Adjust tone, language, and flow." },
                                    { title: "Apply theme or brand style", desc: "Maintain visual consistency." },
                                    { title: "Edit slides with advanced AI tools", desc: "Polish each slide effortlessly." }
                                ].map((step, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.1 }}
                                        className="flex items-start gap-4"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-[#2563EB] font-bold text-sm shrink-0 mt-1">
                                            {i + 1}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900">{step.title}</h4>
                                            <p className="text-gray-500 text-sm mt-1">{step.desc}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <div className="mt-10">
                                <Link href="/signup" className="px-6 py-3 bg-gray-900 text-white rounded-lg font-bold text-sm hover:bg-gray-800 transition-colors shadow-lg shadow-gray-200 inline-block">
                                    Start Creating Now
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PartnershipProposalBuilder;
